import type { TranslateFn } from '../i18n/useAppTranslation';
import { MAP_SHEET_TEMPLATE } from '../constants/csvTemplates';
import { canonicalSheetTemplateParam, templateColumns } from './csvTemplate';

/**
 * The canonical MAP / dealer sheet layout, for the store's MAP Pricing Data upload (`POST
 * /pricing-ingestion/upload`) and the vendor's private dealer sheet (`POST /vendor/private-pricing/upload`).
 * Both end in `pricingIngestionService.parseMotzuSheet` when sent with `?template=motzu`, which reads
 * these headers (`src/services/pricingIngestionService.ts`): Model (required, `modelColumn`), Brand,
 * MAP Price, MSRP, Instant Rebate, Trade Credit, Effective Date (rebate start), End Date (rebate end).
 * It has no recogniser for UPC or dealer price, so neither is in the template.
 */
export const MAP_SHEET_HEADERS = MAP_SHEET_TEMPLATE.headers;

/** Parser requirement per column (only Model is read strictly: a row may carry just a rebate or trade credit); the vendor's sheet needs Brand because rows for any other brand are dropped. */
const REQUIRED: Record<'mapSheet' | 'vendorSheet', Record<string, boolean>> = {
  mapSheet: { Model: true },
  vendorSheet: { Model: true, Brand: true },
};

export const mapSheetTemplateProps = (t: TranslateFn, surface: 'mapSheet' | 'vendorSheet') => ({
  fileName: MAP_SHEET_TEMPLATE.fileName,
  columns: templateColumns(
    MAP_SHEET_TEMPLATE.headers,
    MAP_SHEET_TEMPLATE.rows,
    Object.fromEntries(
      MAP_SHEET_TEMPLATE.headers.map((name) => [
        name,
        { required: REQUIRED[surface][name], description: t(`csvTemplate.${surface}.${name}`) },
      ]),
    ),
  ),
  exampleRows: MAP_SHEET_TEMPLATE.rows.map((row) => [...row]),
  intro: t(`csvTemplate.${surface}.intro`),
});

/** `template=motzu` for a CSV in the downloadable layout; see `canonicalSheetTemplateParam`. */
export const mapSheetTemplateParam = (file: File) => canonicalSheetTemplateParam(file, MAP_SHEET_TEMPLATE.headers);
